import { gql, useMutation } from '@apollo/client';
import {
  Alert,
  Button,
  ButtonGroup,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import React, { useCallback, useState } from 'react';

import { useGraphRef } from 'src/app/graph/hooks/useGraphRef';
import { cloudSettingsRouteConfig } from 'src/app/graph/routes';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

export const DeleteSecretModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { deleteSecretModalState } = useRouteParams(cloudSettingsRouteConfig);
  const { secretName } = deleteSecretModalState ?? {};
  const graphRef = useGraphRef();
  const [isDeletingSecret, setIsDeletingSecret] = useState(false);

  const [deleteSecret, { data: deleteSecretResponseData, error }] = useMutation<
    GraphQLTypes.DeleteSecretModalSetSecretMutation,
    GraphQLTypes.DeleteSecretModalSetSecretMutationVariables
  >(
    gql`
      mutation CloudDeleteSecretModalSetSecretMutation(
        $graphId: ID!
        $variant: String!
        $input: RouterSecretsInput!
      ) {
        service(id: $graphId) {
          variant(name: $variant) {
            router {
              setSecrets(input: $input) {
                ... on RouterSecretsSuccess {
                  secrets {
                    createdAt
                    name
                    hash
                  }
                }
                ... on InvalidInputErrors {
                  message
                  errors {
                    reason
                    argument
                  }
                }
                ... on InternalServerError {
                  message
                }
              }
            }
          }
        }
      }
    `,
    {
      variables: {
        graphId: graphRef?.graphId ?? '',
        variant: graphRef?.graphVariant ?? '',
        input: {
          unsetSecrets: [secretName ?? ''],
        },
      },
    },
  );

  const handleDeletion = useCallback(async () => {
    if (!graphRef) return;
    setIsDeletingSecret(true);
    const { data: responseData } = await deleteSecret({
      update: (cache) => {
        cache.evict({
          id: cache.identify({
            __typename: 'Service',
            id: graphRef.graphId,
          }),
          fieldName: 'variant',
        });
      },
    });
    if (
      responseData?.service?.variant?.router?.setSecrets?.__typename ===
      'RouterSecretsSuccess'
    ) {
      onClose();
    }
    setIsDeletingSecret(false);
  }, [deleteSecret, graphRef, onClose]);

  const deleteSecretMutationResponseHasErrors =
    deleteSecretResponseData?.service?.variant?.router?.setSecrets
      ?.__typename === 'InternalServerError' ||
    deleteSecretResponseData?.service?.variant?.router?.setSecrets
      ?.__typename === 'InvalidInputErrors' ||
    error;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        {secretName ? (
          <>
            <ModalHeader>
              <ModalTitle>Delete {secretName}?</ModalTitle>
            </ModalHeader>
            <ModalBody>
              {deleteSecretMutationResponseHasErrors && <ErrorMessage />}
              This can't be undone.
            </ModalBody>
            <ModalFooter>
              <ButtonGroup>
                <Button variant="secondary" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  isLoading={isDeletingSecret}
                  loadingText="Deleting"
                  onClick={handleDeletion}
                >
                  Yes, delete
                </Button>
              </ButtonGroup>
            </ModalFooter>
          </>
        ) : (
          <ModalBody>
            <Alert status="error">
              There was an error retrieving your secret. Please try again or
              contact us for assistance.
            </Alert>
          </ModalBody>
        )}
      </ModalContent>
    </Modal>
  );
};
